import React, { forwardRef, useCallback, useRef, useState, useEffect, useMemo } from "react";
import { Input } from "antd";
import classNames from "classnames";
import _ from "lodash";
import makeCancellablePromise from "make-cancellable-promise";
import ACButton from "ac-components/ac-button/ACButton";
import useAnyaDraft from "hooks/useAnyaDraft";
import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";
import LoadingIcon from "ac-components/ac-icons/LoadingIcon";
import AnyaMessage from "./AnyaMessage";
import StreamingText from "./StreamingText";
import AnyaTicketCreationConfirmationPopup from "./AnyaTicketCreation/AnyaTicketCreationConfirmationPopup";
import { ANYA_POLLING_CONFIG, dummySuggestedQuestions, dummySuggestedQuestionsForAnyaSidebar } from "./anyaConstants";
import { hasPollingTimedOut, isStatusAwaitingConfirmation, isStatusInProgress } from "./anyaUtils";
import AIPersona from "./AIPersona";
import AnyaIcon from "ac-components/ac-icons/AnyaIcon";
import ThumbsUpIcon from "ac-components/ac-icons/ThumbsUpIcon";
import ThumbsDownIcon from "ac-components/ac-icons/ThumbsDownIcon";
import CopyIcon from "ac-components/ac-icons/CopyIcon";
import RefreshIcon from "ac-components/ac-icons/RefreshIcon";
import {
    cancelRequest,
    getAnyaConversationMessages,
    getAnyaMessage,
    retryRequest,
    sendConfirmation,
    storeFeedback,
} from "restAPI/AnyaAPI";
import message from "components/CustomMessage";
import moment from 'moment-timezone';
import { AnyaSessionMode } from "types/anya";
import './AnyaSummary.less';

function EmptyChat({ sessionMode, selectedAgent }) {
    if (sessionMode === AnyaSessionMode.AGENT) {
        return (
            <div className="anya-summary-empty-chat-wrapper">
                <div>
                    <AnyaIcon style={{ fontSize: 96, color: '#D5D9DE' }} />
                </div>
                <div className="anya-summary-empty-chat-instruction">
                    <div className="anya-summary-empty-chat-instruction-header">
                        {selectedAgent?.name || "Agent Chat"}
                    </div>
                    <div className="anya-summary-empty-chat-instruction-desciption">
                        {selectedAgent?.description || "Ask this agent a question to get started."}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="anya-summary-empty-chat-wrapper">
            <div>
                <AnyaIcon style={{ fontSize: 200, color: '#D5D9DE' }} />
            </div>
            <div className="anya-summary-empty-chat-instruction">
                <div className="anya-summary-empty-chat-instruction-header">Welcome to Anya</div>
                <div className="anya-summary-empty-chat-instruction-desciption">Type or select a question below</div>
            </div>
        </div>
    );
}

const COLORS = ['#54ca8b', '#feb96a', '#fc8f67', '#e05252', '#FF8042', '#FFBB28', '#00C49F', '#0088FE', '#FF8042', '#FFBB28'];

const AnyaConversation = forwardRef(function AnyaConversation(props, ref) {
    const {
        chatId,
        isNewChat,
        title = null,
        widgetType,
        dashboardId,
        fetchChatHistory,
        setChatId,
        messageContainerRef,
        showSuggestedQuestions = true,
        defaultPromptPrefix = "Tell me about ",
        startWithEmptyPrompt = false,
        invocationContext,
        sessionMode = AnyaSessionMode.CHAT,
        selectedAgent = null,
        onConversationMetadata,
    } = props;

    const inputRef = useRef(null);
    const { initialDraft, saveDraft, clearDraft } = useAnyaDraft();
    const [messages, setMessages] = useState([]);
    const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
    const [personaId, setPersonaId] = useState(null);
    const [inputValue, setInputValue] = useState(initialDraft);
    const [showThumbsDownReasonTextArea, setShowThumbsDownReasonTextArea] = useState({});
    const getInitialSuggestedQuestions = useCallback(() => {
        if (sessionMode === AnyaSessionMode.AGENT) {
            return selectedAgent?.starterPrompts || [];
        }

        return title ? dummySuggestedQuestions : dummySuggestedQuestionsForAnyaSidebar;
    }, [selectedAgent, sessionMode, title]);
    const [suggestedQuestions, setSuggestedQuestions] = useState(getInitialSuggestedQuestions);
    const [loading, setLoading] = useState(false);
    const [messageId, setMessageId] = useState("");
    const [feedbackText, setFeedbackText] = useState("");
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const [showTicketConfirmation, setShowTicketConfirmation] = useState({});
    const [confirmationData, setConfirmationData] = useState({});
    const [confirmationRequestId, setConfirmationRequestId] = useState({});
    const [activeFeedback, setActiveFeedback] = useState({});
    const isRetryingRef = useRef(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [currentRequestId, setCurrentRequestId] = useState(null);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const conversationPollingRef = useRef({ intervalId: null, cancel: null, startTime: null });
    const isHydratingNewChatRef = useRef(false);
    const historyPaginationRef = useRef({ nextPage: 0, hasNext: true, isFetching: false });
    const historyLoadCancelRef = useRef(() => {});
    const latestRetryContextRef = useRef({
        chatId,
        sessionMode,
        selectedAgent,
        lastMessage: null,
    });

    latestRetryContextRef.current = {
        chatId,
        sessionMode,
        selectedAgent,
        lastMessage: messages[messages.length - 1] || null,
    };

    const hasInProgressMessage = useMemo(() => {
        return messages.some(message => isStatusInProgress(message));
    }, [messages]);

    const focusInput = () => {
        // Focus the input field once the component mounts,to avoid background distortion
        // setTimeout is needed to prevent the input from being focused immediately
        setTimeout(() => {
            try {
                inputRef.current?.focus();
            } catch (error) {
                console.error('Focus error:', error);
            }
        }, 300);
    };

    const scrollToBottom = useCallback(() => {
        if (messageContainerRef.current && typeof messageContainerRef.current.scrollTo === 'function') {
            messageContainerRef.current.scrollTo({
                top: messageContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [messageContainerRef]);

    const pollLatestMessage = useCallback(async (messageIdToPoll) => {
        if (conversationPollingRef.current.intervalId) {
            clearInterval(conversationPollingRef.current.intervalId);
        }
        conversationPollingRef.current.startTime = Date.now();
        const pollInterval = setInterval(async () => {
            try {
                // Check timeout - elapsed time since polling started
                if (hasPollingTimedOut(conversationPollingRef.current.startTime, ANYA_POLLING_CONFIG.MAX_POLL_TIME)) {
                    console.warn("Max poll time reached for conversation polling. Stopping polling.");
                    clearInterval(conversationPollingRef.current.intervalId);
                    conversationPollingRef.current.intervalId = null;
                    conversationPollingRef.current.startTime = null;
                    setIsGeneratingResponse(false);

                    // Update message with timeout error
                    setMessages(prevMessages => {
                        const updatedMessages = [...prevMessages];
                        if (updatedMessages.length > 0) {
                            const existingMessage = updatedMessages[updatedMessages.length - 1];
                            updatedMessages[updatedMessages.length - 1] = {
                                ...existingMessage,
                                response: {
                                    ...existingMessage.response,
                                    reasoningSummary: "Unable to generate response, please try again.",
                                    status: "Error"
                                },
                                status: "Error"
                            };
                        }
                        return updatedMessages;
                    });
                    return;
                }
                const { promise, cancel } = getAnyaMessage({
                    conversationId: chatId, 
                    messageId: messageIdToPoll
                });
                conversationPollingRef.current.cancel = cancel;
                const { data: latestMessage } = await promise;
                if (latestMessage) {
                    const transformedMessage = {
                        response: {
                            reasoningSummary: latestMessage.reasoningSummary,
                            details: latestMessage.details,
                            visualisation: latestMessage.visualisation,
                            nextQuestions: latestMessage.nextQuestions,
                            status: latestMessage.status,
                            eventData: latestMessage.eventData,
                            displayStatus: latestMessage.displayStatus
                        },
                        messageId: messageIdToPoll,
                        status: latestMessage.status,
                        displayStatus: latestMessage.displayStatus
                    };
                    // Always update the message to show current state (including loading states)
                    setMessages(prevMessages => {
                        const updatedMessages = [...prevMessages];
                        if (updatedMessages.length > 0) {
                            const existingMessage = updatedMessages[updatedMessages.length - 1];
                            updatedMessages[updatedMessages.length - 1] = {
                                ...existingMessage,
                                ...transformedMessage,
                                userMessage: existingMessage.userMessage,
                                isThumbsUp: existingMessage.isThumbsUp
                            };
                        }
                        return updatedMessages;
                    });

                    // Check if message is complete (not In Progress)
                    if (!isStatusInProgress(latestMessage)) {
                        // Check if status is AWAITING_CONFIRMATION
                        if (isStatusAwaitingConfirmation(latestMessage)) {
                            // Set ticket confirmation state
                            setShowTicketConfirmation(prev => ({ ...prev, [chatId]: true }));
                            setConfirmationData(prev => ({ ...prev, [chatId]: latestMessage.eventData }));
                            setConfirmationRequestId(prev => ({ ...prev, [chatId]: messageIdToPoll }));
                        }

                        // Stop polling and update state
                        clearInterval(conversationPollingRef.current.intervalId);
                        conversationPollingRef.current.intervalId = null;
                        conversationPollingRef.current.startTime = null;
                        setIsGeneratingResponse(false);

                        // Update suggested questions if available
                        if (latestMessage.nextQuestions) {
                            setSuggestedQuestions(latestMessage.nextQuestions);
                        }

                        setTimeout(() => {
                            scrollToBottom();
                        }, 100);
                    }
                }
            } catch (error) {
                console.error('Error polling latest message:', error);
                clearInterval(conversationPollingRef.current.intervalId);
                conversationPollingRef.current.intervalId = null;
                conversationPollingRef.current.startTime = null;
                setIsGeneratingResponse(false);
            }
        }, ANYA_POLLING_CONFIG.SUBSEQUENT_POLL_INTERVAL);

        conversationPollingRef.current.intervalId = pollInterval;
    }, [chatId, setIsGeneratingResponse, setMessages, setSuggestedQuestions, scrollToBottom, setShowTicketConfirmation, setConfirmationData, setConfirmationRequestId]);

    const handleSend = (question) => {
        const messageText = question || inputValue;
        if (messageText.trim()) {
            const userMessage = { text: messageText, type: 'user' };
            setMessages(prev => [...prev, { userMessage, response: null }]);
            setInputValue("");
            clearDraft();
            setIsGeneratingResponse(true);
            if (isNewChat && !chatId) {
                isHydratingNewChatRef.current = true;
            }
        }
        scrollToBottom();
    };

    const handleStopRequest = useCallback(async () => {
        setIsCancelling(true);
        const lastMessage = messages[messages.length - 1];
        const userQuery = lastMessage?.userMessage?.text || "";

        let cancelMessage = "Request cancelled";
        try {
            // Only call cancel API if we have the identifiers required by backend
            if (currentRequestId && currentConversationId) {
                const apiResponse = await cancelRequest({
                    query: userQuery,
                    requestId: currentRequestId,
                    conversationId: currentConversationId,
                }).promise;

                // On receiving 200 status code, reload chat as per requirements
                if (chatId) {
                    fetchChatHistory(false);
                }

                // Use the actual response message from API (if present)
                cancelMessage = apiResponse.data?.data || cancelMessage;
                message.success(cancelMessage);
            } else {
                message.info("Cancelling request...");
            }
        } catch (error) {
            message.error("Failed to cancel request. Please try again.");
        } finally {
            setIsCancelling(false);
            // Reset button state regardless of API outcome
            setIsGeneratingResponse(false);
            setCurrentRequestId(null);
            setCurrentConversationId(null);

            // Update the last message to show cancellation with API response (or default)
            setMessages((prev) => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (lastIndex >= 0) {
                    updated[lastIndex] = {
                        ...updated[lastIndex],
                        response: {
                            reasoningSummary: cancelMessage,
                            details: "",
                            visualisation: [],
                        },
                        messageId: currentRequestId || updated[lastIndex].messageId,
                        isCancelled: true,
                    };
                }
                return updated;
            });
            scrollToBottom();
        }
    }, [isCancelling, currentRequestId, currentConversationId, chatId, fetchChatHistory, messages, setIsGeneratingResponse, setMessages, scrollToBottom]);

    const handleRequestStart = useCallback((cancelFn, requestId, conversationId) => {
        if (requestId && conversationId) {
            setCurrentRequestId(requestId);
            setCurrentConversationId(conversationId);

        }
    }, []);

    const handleRequestComplete = useCallback(() => {
        setCurrentRequestId(null);
        setCurrentConversationId(null);

        // Use MutationObserver to detect when DOM changes are complete
        if (messageContainerRef.current) {
            const observer = new window.MutationObserver((mutations) => {
                let shouldScroll = false;

                mutations.forEach((mutation) => {
                    // Check for added nodes (new content)
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        shouldScroll = true;
                    }

                    // Check for attribute changes that might affect layout
                    if (mutation.type === 'attributes' &&
                        ['class', 'style', 'data-status'].includes(mutation.attributeName)) {
                        shouldScroll = true;
                    }

                    // Check for text content changes
                    if (mutation.type === 'characterData') {
                        shouldScroll = true;
                    }
                });

                if (shouldScroll) {
                    // Small delay to ensure layout calculations are complete
                    window.requestAnimationFrame(() => {
                        scrollToBottom();
                        // Disconnect observer after scrolling
                        observer.disconnect();
                    });
                }
            });

            // Start observing the message container
            observer.observe(messageContainerRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'style', 'data-status'],
                characterData: true
            });

            // Fallback timeout in case no mutations are detected
            const fallbackTimeout = setTimeout(() => {
                observer.disconnect();
                scrollToBottom();
            }, 500);

            const cleanup = () => {
                observer.disconnect();
                clearTimeout(fallbackTimeout);
            };

            return cleanup;
        } else {
            // Fallback to original behavior if no container ref
            setTimeout(() => {
                scrollToBottom();
            }, 200);
        }
    }, [scrollToBottom, messageContainerRef]);

    const handleRetryComplete = useCallback((messageId) => {
        // When Retrying is done (either fails or succeeds), make the isRetrying property of the message as false so that this message is not retried again when we retry/send a new message.
        setMessages(prev => prev.map(msg =>
            msg.messageId === messageId
                ? { ...msg, isRetrying: false }
                : msg
        ));

        // Clean up cancellation infrastructure when retry completes
        setCurrentRequestId(null);
        setCurrentConversationId(null);
    }, [setMessages]);

    const onFeedbackOptionClick = useCallback(async (isThumbsUp, messageId, conversationId, feedback, index) => {
        const feedbackObject = { requestId: messageId, isThumbsUp, feedback };
        setActiveFeedback({ ...activeFeedback, [messageId]: isThumbsUp });
        setShowThumbsDownReasonTextArea((prev) => ({
            ...prev,
            [messageId]: !isThumbsUp,
        }));
        try {
            await storeFeedback(conversationId, feedbackObject).promise;
            message.success("Feedback Submitted");
            if (index === messages.length - 1) {
                scrollToBottom();
            }
        } catch {
            message.error("Error submitting feedback. Please try again.");
        }
    }, [activeFeedback, messages, scrollToBottom]);

    const onSubmitClick = useCallback(async (messageId, conversationId) => {
        setShowThumbsDownReasonTextArea((prev) => ({ ...prev, [messageId]: false }))
        const feedbackObject = {
            requestId: messageId,
            isThumbsUp: false,
            feedback: feedbackText
        }
        try{
            await storeFeedback(conversationId, feedbackObject).promise;
            message.success("Feedback Submitted");
        } catch {
            // already handled with axios
        }
        setFeedbackText("");
    }, [feedbackText]);

    const [messageResponses, setMessageResponses] = useState({}); // Store Response details

    const handleResponseDetails = useCallback((messageId, details) => {
        setMessageResponses((prev) => ({ ...prev, [messageId]: details }));
    }, []);

    const onCopyClick = (messageDetails, messageId) => {
        const details = messageDetails || messageResponses[messageId];
        navigator.clipboard.writeText(details)
                .then(() => {
                    setCopiedMessageId(messageId);
                    setTimeout(() => {
                        setCopiedMessageId(null);
                    }, 2000);
                })
                .catch(() => {
                    message.error("Failed to copy message. Please try again.");
                });
    };

    const onRetryClick = useCallback(async (messageId) => {
        try {
            // Prevent multiple retries using ref for immediate check
            if (isRetryingRef.current) return;

            isRetryingRef.current = true;

            const {
                chatId: currentChatId,
                sessionMode: currentSessionMode,
                selectedAgent: currentSelectedAgent,
                lastMessage,
            } = latestRetryContextRef.current;
            const originalRequestId = lastMessage?.messageId;

            if (!originalRequestId || !currentChatId) {
                message.error("Unable to retry. Missing request information.");
                return;
            }

            setIsGeneratingResponse(true);

            // Call the retry API
            const userLocale = navigator.language || "en-US";
            const userZoneId = moment.tz.guess();
            const normalizedTitle = title || "";
            
            const { promise } = retryRequest({
                query: lastMessage?.userMessage?.text || "",
                widgetTitle: normalizedTitle,
                widgetType: widgetType,
                dashboardId: dashboardId,
                conversationId: currentChatId,
                requestId: originalRequestId,
                locale: userLocale,
                zoneId: userZoneId,
                ...(invocationContext ? { invocationContext } : {}),
                ...(currentSessionMode === AnyaSessionMode.AGENT && currentSelectedAgent?.id
                    ? { agentId: currentSelectedAgent.id }
                    : {}),
            });

            const response = await promise;
            const newRequestId = response.data.requestId; // Get the NEW ID from the response

            setCurrentRequestId(newRequestId);
            setCurrentConversationId(currentChatId);

            //update the message with the NEW ID and the flag to trigger polling.- CORRECT POLLING
            setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (lastIndex >= 0) {
                    updated[lastIndex] = {
                        ...updated[lastIndex],
                        messageId: newRequestId, // Use the new ID
                        isRetrying: true,        // NOW we trigger the polling logic
                        isCancelled: false,      // Clear cancelled flag for retry
                        response: null
                    };
                }
                return updated;
            });

            // The child component's polling state will provide feedback from here.
            setIsGeneratingResponse(false);

        } catch (error) {
            console.error("Error retrying message:", error);
            message.error("Failed to retry. Please try again.");
            setIsGeneratingResponse(false);
            setCurrentRequestId(null);
            setCurrentConversationId(null);
        } finally {
            // Always reset retry state in finally block
            isRetryingRef.current = false;
        }
    }, [dashboardId, invocationContext, setIsGeneratingResponse, setMessages, title, widgetType]);

    useEffect(() => {
        if (!loading && !isGeneratingResponse) {
            focusInput();
        }
    }, [loading, isGeneratingResponse, isNewChat, chatId]);

    useEffect(() => {
        // Only reset input when explicitly starting a new chat AND there's no saved draft
        // The draft should persist across Anya open/close cycles
        if (isNewChat && startWithEmptyPrompt && !initialDraft) {
            setInputValue("");
        }
    }, [isNewChat, startWithEmptyPrompt, initialDraft]);

    useEffect(() => {
        messages.forEach((message) => {
            const mId = message.messageId ? message.messageId : messageId;
            const isThumbsUp = message.isThumbsUp;
            if (!_.isEmpty(mId)) {
                // check if the messageId is already in the activeFeedback object with true or false, if it is
                // then do not update the activeFeedback object. 
                setActiveFeedback((prev) => {
                    if (prev[mId] === true || prev[mId] === false) {
                        return prev;
                    }
                    return {
                        ...prev,
                        [mId]: isThumbsUp === undefined ? null : isThumbsUp
                    };
                });
            }
        })
    }, [messageId, messages])

    useEffect(() => {
        const cancalMethods = [];
        historyLoadCancelRef.current();
        historyPaginationRef.current = {
            nextPage: 0,
            hasNext: true,
            isFetching: false,
        };

        if (isNewChat || !chatId) {
            return () => {
                cancalMethods.forEach((cancelRes) => cancelRes());

                if (conversationPollingRef.current.intervalId) {
                    clearInterval(conversationPollingRef.current.intervalId);
                }
                if (conversationPollingRef.current.cancel) {
                    conversationPollingRef.current.cancel();
                    conversationPollingRef.current.cancel = null;
                }
            };
        }

        const size = 5;
        const shouldPreserveCurrentMessages = isHydratingNewChatRef.current;
        if (!shouldPreserveCurrentMessages) {
            setMessages([]);
            setSuggestedQuestions([]);
        }
        setIsGeneratingResponse(true);
        setPersonaId(null);
        let cancelWrappedHistoryLoad = () => {};
        const cancelActiveHistoryLoad = () => {
            cancelWrappedHistoryLoad();
        };
        const isActiveHistoryRequest = () => historyLoadCancelRef.current === cancelActiveHistoryLoad;
        historyLoadCancelRef.current = cancelActiveHistoryLoad;

        const { promise: historyLoadPromise, cancel: cancelHistoryLoad } = makeCancellablePromise((async () => {
            const fetchConversationPage = async ({ prepend = false } = {}) => {
                if (!isActiveHistoryRequest()) {
                    return null;
                }

                if (historyPaginationRef.current.isFetching) {
                    return null;
                }

                if (prepend && !historyPaginationRef.current.hasNext) {
                    return null;
                }

                const nextPage = historyPaginationRef.current.nextPage;
                historyPaginationRef.current.isFetching = true;

                try {
                    setLoading(true);
                    const request = getAnyaConversationMessages({
                        conversationId: chatId,
                        page: nextPage,
                        size,
                    });
                    const cancelRequest = () => {
                        request.cancel();
                    };
                    cancalMethods.push(cancelRequest);
                    const { promise, cancel } = makeCancellablePromise(request.promise);
                    cancalMethods.push(cancel);
                    const { data } = await promise;
                    if (!isActiveHistoryRequest()) {
                        return null;
                    }

                    historyPaginationRef.current.nextPage = nextPage + 1;
                    historyPaginationRef.current.hasNext = (data?.conversations?.length || 0) >= size;

                    if (prepend) {
                        setMessages(prevMessages => [...(data?.conversations || []), ...prevMessages]);
                    } else {
                        setMessages(data?.conversations || []);
                        setPersonaId(data?.personaId);
                        if (data?.sessionType || data?.agentId || data?.agent?.id) {
                            onConversationMetadata?.(data);
                        }

                        if (!_.isEmpty(data?.conversations)) {
                            const lastMessage = data?.conversations[data?.conversations.length - 1];
                            setCurrentRequestId(lastMessage.messageId);
                            setCurrentConversationId(chatId);
                            if (isStatusInProgress(lastMessage)) {
                                setIsGeneratingResponse(true);
                                pollLatestMessage(lastMessage.messageId);
                            } else if (isStatusAwaitingConfirmation(lastMessage.response)) {
                                setShowTicketConfirmation(prev => ({ ...prev, [chatId]: true }));
                                setConfirmationData(prev => ({ ...prev, [chatId]: lastMessage.response.eventData }));
                                setConfirmationRequestId(prev => ({ ...prev, [chatId]: lastMessage.messageId }));
                                setIsGeneratingResponse(false);
                            } else {
                                setIsGeneratingResponse(false);
                            }
                        } else {
                            setCurrentRequestId(null);
                            setCurrentConversationId(null);
                            setIsGeneratingResponse(false);
                        }

                        if (!_.isEmpty(data?.conversations)) {
                            setSuggestedQuestions(data?.conversations[data?.conversations.length - 1].response?.nextQuestions);
                        }
                    }

                    return data;
                } catch (e) {
                    if (prepend && isActiveHistoryRequest()) {
                        message.error("Error while loading messages");
                    }
                    if (prepend) {
                        return null;
                    }
                    throw e;
                } finally {
                    if (isActiveHistoryRequest()) {
                        historyPaginationRef.current.isFetching = false;
                        setLoading(false);
                    }
                }
            };

            const handleScroll = (e) => {
                const { scrollTop, scrollHeight } = e.target;
                if (scrollTop === 0 && historyPaginationRef.current.hasNext && !historyPaginationRef.current.isFetching) {
                    const currentHeight = scrollHeight;
                    fetchConversationPage({ prepend: true }).then((data) => {
                        if (!data) {
                            return;
                        }
                        e.target.scrollTop = e.target.scrollHeight - currentHeight;
                    });
                }
            };

            try {
                setLoading(true)
                setShowThumbsDownReasonTextArea((prev) => {
                    // assign false to every messageId so that text area is visible for none when fetchmessage is called.
                    const updatedState = Object.keys(prev).reduce((acc, key) => {
                        acc[key] = false;
                        return acc;
                    }, {});

                    return updatedState;
                });
                const data = await fetchConversationPage();
                if (!data || !isActiveHistoryRequest()) {
                    return;
                }
                setTimeout(() => {
                    scrollToBottom();
                }, 5);
                const container = messageContainerRef.current;
                if (container) {
                    container.addEventListener("scroll", handleScroll);
                    cancalMethods.push(() => {
                        container.removeEventListener("scroll", handleScroll);
                    });
                }
            }
            catch (e) {
                if (isActiveHistoryRequest()) {
                    const loadErrorMessage = e?.response?.data?.message || e?.message || "Error while loading messages";
                    message.error(loadErrorMessage);
                }
            }
            finally {
                if (isActiveHistoryRequest()) {
                    isHydratingNewChatRef.current = false;
                    setLoading(false)
                }
            }
        })());
        cancelWrappedHistoryLoad = cancelHistoryLoad;
        cancalMethods.push(cancelActiveHistoryLoad);
        historyLoadPromise.catch(() => {});

        return () => {
            if (historyLoadCancelRef.current === cancelActiveHistoryLoad) {
                historyLoadCancelRef.current = () => {};
            }
            cancalMethods.forEach((cancelRes) => cancelRes());

            if (conversationPollingRef.current.intervalId) {
                clearInterval(conversationPollingRef.current.intervalId);
            }
            if (conversationPollingRef.current.cancel) {
                conversationPollingRef.current.cancel();
                conversationPollingRef.current.cancel = null;
            }
        };
    }, [chatId, isNewChat, messageContainerRef, onConversationMetadata, pollLatestMessage, scrollToBottom]);

    useEffect(() => {
        if (!isNewChat) {
            return;
        }

        historyLoadCancelRef.current();
        historyLoadCancelRef.current = () => {};
        historyPaginationRef.current = {
            nextPage: 0,
            hasNext: true,
            isFetching: false,
        };
        setMessages([]);
        setCurrentRequestId(null);
        setCurrentConversationId(null);
        setIsGeneratingResponse(false);
        setPersonaId(null);
        setSuggestedQuestions(getInitialSuggestedQuestions());
        if (typeof messageContainerRef.current?.scrollTo === 'function') {
            messageContainerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
        if (sessionMode !== AnyaSessionMode.AGENT && !startWithEmptyPrompt && (title || dashboardId)) {
            setMessages([{
                "userMessage": {
                    "text": defaultPromptPrefix + (title || dashboardId),
                    "type": "user"
                },
                "response": null
            }]);
        }
    }, [dashboardId, defaultPromptPrefix, getInitialSuggestedQuestions, isNewChat, messageContainerRef, sessionMode, startWithEmptyPrompt, title]);

    const handleTicketConfirm = useCallback(async () => {
        try {
            await sendConfirmation({
                event: { isConfirmed: true },
                requestId: confirmationRequestId[chatId],
                conversationId: chatId
            }).promise;

            message.success("Processing confirmed action");
            setShowTicketConfirmation(prev => ({ ...prev, [chatId]: false }));
            setIsGeneratingResponse(true);
            pollLatestMessage(confirmationRequestId[chatId]);
        } catch (error) {
            console.error('Error confirming action:', error);
            message.error("Failed to process your confirmation");
        }
    }, [chatId, confirmationRequestId, setIsGeneratingResponse, pollLatestMessage]);

    const handleTicketCancel = useCallback(async () => {
        try {
            await sendConfirmation({
                event: { isConfirmed: false },
                requestId: confirmationRequestId[chatId],
                conversationId: chatId
            }).promise;
            setShowTicketConfirmation(prev => ({ ...prev, [chatId]: false }));
            setIsGeneratingResponse(true);
            pollLatestMessage(confirmationRequestId[chatId]);
        } catch (error) {
            console.error('Error cancelling action:', error);
            message.error("Failed to process your cancellation");
        }
    }, [chatId, confirmationRequestId, setIsGeneratingResponse, pollLatestMessage]);

    return (
        <>
            <div className="anya-container h-100" ref={messageContainerRef}>
                <div className="p-t-m">
                    {loading && !isHydratingNewChatRef.current ? <div className="text-center p-l"><LoadingIcon /></div> : null}
                    {
                        !_.isEmpty(messages) ? (
                            messages.map((message, index) => {
                                const messageIdToSend = message.messageId || messageId;
                                const isLastMessage = index === messages.length - 1;
                                return (
                                    <React.Fragment key={index}>
                                        <AnyaMessage
                                            fetchChatHistory={fetchChatHistory}
                                            isNewChat={isNewChat}
                                            title={title}
                                            widgetType={widgetType}
                                            dashboardId={dashboardId}
                                            setIsGeneratingResponse={setIsGeneratingResponse}
                                            chatId={chatId}
                                            setChatId={setChatId}
                                            setSuggestedQuestions={setSuggestedQuestions}
                                            message={message}
                                            index={index}
                                            isLastMessage={isLastMessage}
                                            setShowThumbsDownReasonTextArea={setShowThumbsDownReasonTextArea}
                                            COLORS={COLORS}
                                            setMessageId={setMessageId}
                                            onResponseDetails={handleResponseDetails}
                                            showTicketConfirmation={showTicketConfirmation[chatId]}
                                            setShowTicketConfirmation={(value) => setShowTicketConfirmation(prev => ({ ...prev, [chatId]: value }))}
                                            setConfirmationData={(value) => setConfirmationData(prev => ({ ...prev, [chatId]: value }))}
                                            setConfirmationRequestId={(value) => setConfirmationRequestId(prev => ({ ...prev, [chatId]: value }))}
                                            onRequestStart={handleRequestStart}
                                            onRequestComplete={handleRequestComplete}
                                            onRetryComplete={handleRetryComplete}
                                            invocationContext={invocationContext}
                                            activeAgentId={sessionMode === AnyaSessionMode.AGENT ? selectedAgent?.id : undefined}
                                        />
                                        {!isGeneratingResponse && !isRetryingRef.current && !isStatusInProgress(message) && !isStatusAwaitingConfirmation(message.response) && !showTicketConfirmation[chatId] && (
                                            <div className="feedback-container">
                                                <div className="d-flex align-items-center">
                                                    <ACButtonGroup size="sm">
                                                        <ACButtonGroupItem
                                                            className={classNames({ "feedback-button-selected": activeFeedback[messageIdToSend] === true })}
                                                            onClick={() => onFeedbackOptionClick(true, messageIdToSend, chatId, null, index)}>
                                                            <ThumbsUpIcon />
                                                        </ACButtonGroupItem>
                                                        <ACButtonGroupItem
                                                            className={classNames({ "feedback-button-selected": activeFeedback[messageIdToSend] === false })}
                                                            onClick={() => onFeedbackOptionClick(false, messageIdToSend, chatId, feedbackText, index)}>
                                                            <ThumbsDownIcon />
                                                        </ACButtonGroupItem>
                                                        <ACButtonGroupItem onClick={() => onCopyClick(message?.response?.details, messageIdToSend)}>
                                                            <CopyIcon />
                                                        </ACButtonGroupItem>
                                                        {isLastMessage && (
                                                            <ACButtonGroupItem
                                                                onClick={() => onRetryClick(messageIdToSend)}
                                                                disabled={isRetryingRef.current}
                                                            >
                                                                <RefreshIcon />
                                                            </ACButtonGroupItem>
                                                        )}
                                                    </ACButtonGroup>
                                                    {copiedMessageId === messageIdToSend && <span className="copy-btn-text">Copied!</span>}
                                                </div>
                                                {
                                                    showThumbsDownReasonTextArea[messageIdToSend] && (
                                                        <>
                                                            <div className="d-flex flex-column feedback-text-area">
                                                                <span style={{ fontWeight: "bold" }}>What could be improved?</span>
                                                                <Input.TextArea
                                                                    style={{ margin: "6px 0px" }}
                                                                    value={feedbackText}
                                                                    onChange={(e) => setFeedbackText(e.target.value)}
                                                                    placeholder="Feedback"
                                                                    autoSize={{ minRows: 3, maxRows: 5 }}
                                                                />
                                                                <span style={{ color: "#70737b" }}>Provide feedback on your expectations and results</span>
                                                                <div className="d-flex justify-content-between m-t-sm">
                                                                    <ACButton style={{ backgroundColor: "transparent", color: "gray" }} onClick={() => setShowThumbsDownReasonTextArea((prev) => ({ ...prev, [messageIdToSend]: false }))}>Cancel</ACButton>
                                                                    <ACButton onClick={() => onSubmitClick(messageIdToSend, chatId)}>Submit</ACButton>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )
                                                }
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            !loading && <EmptyChat sessionMode={sessionMode} selectedAgent={selectedAgent} />
                        )
                    }
                    {showTicketConfirmation[chatId] && (
                        <AnyaTicketCreationConfirmationPopup
                            ticketDetails={confirmationData[chatId]?.message}
                            actionType={confirmationData[chatId]?.type}
                            onConfirm={handleTicketConfirm}
                            onCancel={handleTicketCancel}
                        />
                    )}
                </div>
            </div>
            <div className={classNames('anya-footer', { empty: isGeneratingResponse })}>
                <div className="d-flex justify-content-center">
                    <ChevronDownIcon className="font-size-24 scroll-down-icon" onClick={scrollToBottom} />
                </div>
                {showSuggestedQuestions && <div className="sg-qbox m-t-m">
                    {!isGeneratingResponse && (
                        <div className="suggested-questions-row">
                            {suggestedQuestions.map((question, index) => (
                                <div key={index} className="suggested-questions" onClick={() => handleSend(question)}>
                                    <div title={question}>
                                        <StreamingText style={{ marginBottom: 0 }} text={question} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>}
                <div className="custom-card anya-input-wrapper">
                    <Input ref={inputRef}
                        bordered={false}
                        className="p-0 flex-1 m-r-xs"
                        placeholder="Type your message here..."
                        value={inputValue}
                        onChange={(e) => {
                            const value = e.target.value;
                            setInputValue(value);
                            saveDraft(value);
                        }}
                        onPressEnter={(e) => !e.shiftKey && (e.preventDefault(), handleSend())}
                        disabled={isGeneratingResponse || hasInProgressMessage || showTicketConfirmation[chatId]}
                        autoComplete="off"
                    />
                    {sessionMode !== AnyaSessionMode.AGENT ? (
                        <AIPersona disabled={!(isNewChat && messages.length === 0)} defaultPersonaId={personaId} />
                    ) : null}
                    {(isGeneratingResponse || hasInProgressMessage || showTicketConfirmation[chatId]) ? (
                        <ACButton onClick={handleStopRequest} disabled={showTicketConfirmation[chatId]} loading={isCancelling} className="anya-stop-button">
                           <div className="anya-stop-icon" />
                        </ACButton>
                    ) : (
                        <ACButton onClick={() => handleSend()} className="anya-submit-button" disabled={isGeneratingResponse || hasInProgressMessage || showTicketConfirmation[chatId]}>Ask Anya</ACButton>
                    )}
                </div>
                <div className="anya-disclaimer">Anya uses AI, verify responses.</div>
            </div>
        </>
    );
});

export default AnyaConversation;
